import ActionsButton          from '@/components/actionsButton/ActionsButton';
import Image                  from '@/components/Image/Image';
import { Tag }                from '@/components/ui/tag';
import ContactModel           from '@/models/services/network/ContactModel';
import RelationRequestModel   from '@/models/services/network/RelationRequestModel';
import RelationModel          from '@/models/services/network/RelationModel';
import FileModel              from '@/models/services/file/FileModel';
import { Box }                from '@chakra-ui/react';
import { Text }               from '@chakra-ui/react';
import { HiCheck }            from 'react-icons/hi';
import { useTranslation }     from 'react-i18next';
import { getLocale }          from '@widesk-core/locale';
import useFile                from '@/hooks/useFile';

export interface IEntityContactRowProps {
	contact:                  ContactModel;
	isFirst:                  boolean;
	isGestionnaire:           boolean;
	isManager:                boolean;
	file?:                    FileModel;
	relationRequest?:         RelationRequestModel;
	relation?:                RelationModel;
	onEdit:                   () => void;
	onDelete:                 () => void;
	onUnassign?:              () => void;
	onSendRelationRequest:    () => void;
	onCancelRelationRequest?: () => void;
	onDissociate?:            () => void;
	isReadOnly?:              boolean;
}

const EntityContactRow = ({ contact, isFirst, isGestionnaire, isManager, file, relationRequest, relation, onEdit, onDelete, onUnassign, onSendRelationRequest, onCancelRelationRequest, onDissociate, isReadOnly }: IEntityContactRowProps) => {
	const { t } = useTranslation();
	const { source } = useFile(file);

	const hasRelation        = !!relation;
	const hasRelationRequest = !!relationRequest;
	const requestStatus      = relationRequest?.relationRequestStatus;
	const isPendingRequest   = requestStatus?.reference === 'pending';

	return (
		<Box
			borderTop={isFirst ? undefined : '1px solid'}
			borderColor="gray.100"
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			py="8px"
		>
			<Box display="flex" alignItems="center" gap="15px">
				<Box minWidth="42px" maxWidth="42px" height="42px">
					<Image errorSize="sm" type="profile" borderRadius="full" src={contact.get('photoUrl') || source} width="42px" height="42px" objectFit="cover" />
				</Box>
				<Box display="flex" flexDirection="column" gap="2px">
					<Box display="flex" alignItems="center" gap="6px">
						<Text lineHeight="1" className="poppins-semibold" fontSize="14px">{contact.fullName}</Text>
						{isGestionnaire && (
							<Tag colorPalette="purple" size="sm">{t('pages.entity.gestionnaire.title')}</Tag>
						)}
						{isManager && (
							<Tag colorPalette="blue" size="sm">Manager</Tag>
						)}
					</Box>
					<Text lineHeight="1" fontSize="12px" color="gray.500">{contact.email}</Text>
					{(hasRelation || contact.userUrn) && (
						<Box mt="4px">
							<Tag colorPalette="green" startElement={<HiCheck />} size="sm">
								{t('pages.entity.contacts.linkedToAccount')}
							</Tag>
						</Box>
					)}
					{!hasRelation && !contact.userUrn && hasRelationRequest && requestStatus && (
						<Box mt="4px">
							<Tag colorPalette={requestStatus.color} size="sm">
								{t('pages.entity.contacts.relationRequest')} — {requestStatus.getLabel(getLocale() as 'en' | 'fr')}
							</Tag>
						</Box>
					)}
				</Box>
			</Box>

			{!isGestionnaire && (
				<ActionsButton actions={
					hasRelation
					? [
						{ label: t('pages.entity.contacts.actions.edit'), do: onEdit, disabled: isReadOnly },
						...(isManager && onUnassign ? [{ label: t('app.actions.unassign'), type: 'remove' as const, do: onUnassign, disabled: isReadOnly }] : []),
						{ label: t('pages.entity.contacts.actions.dissociate'), type: 'remove' as const, do: onDissociate! },
					]
					: [
						{ label: t('pages.entity.contacts.actions.edit'), do: onEdit, disabled: isReadOnly },
						...(isManager && onUnassign ? [{ label: t('app.actions.unassign'), type: 'remove' as const, do: onUnassign, disabled: isReadOnly }] : []),
						{ label: t('pages.entity.contacts.actions.sendRelationRequest'), disabled: hasRelationRequest, do: onSendRelationRequest },
						{ label: t('pages.entity.contacts.actions.cancelRelationRequest'), disabled: !isPendingRequest, do: onCancelRelationRequest || (() => {}) },
						{ label: t('pages.entity.contacts.actions.delete'), type: 'remove' as const, do: onDelete, disabled: isReadOnly },
					]
				} />
			)}
		</Box>
	);
};

export default EntityContactRow;
